import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { getWordSetSettings, setActiveWordSet } from "@/api/wordSetSettings";
import { useAuthStore } from "@/store/authStore";

export const wordSetSettingsQueryKey = (userId: string) => ["wordSetSettings", userId] as const;

/** 사용자의 활성 단어장 세트 ID 조회 */
export function useActiveWordSetIdQuery() {
  const userId = useAuthStore(state => state.user?.id);

  return useQuery({
    queryKey: wordSetSettingsQueryKey(userId!),
    queryFn: () => getWordSetSettings(userId!),
    enabled: Boolean(userId),
    select: data => data.find(s => s.is_active)?.set_id ?? null,
  });
}

/** 활성 단어장 세트 변경 뮤테이션 */
export function useSetActiveWordSetMutation() {
  const userId = useAuthStore(state => state.user?.id);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ setId, allSetIds }: { setId: string; allSetIds: string[] }) =>
      setActiveWordSet(userId!, setId, allSetIds),
    onMutate: async ({ setId, allSetIds }) => {
      const key = wordSetSettingsQueryKey(userId!);
      await queryClient.cancelQueries({ queryKey: key });

      const previous = queryClient.getQueryData(key);
      queryClient.setQueryData(
        key,
        allSetIds.map(id => ({
          user_id: userId!,
          set_id: id,
          is_active: id === setId,
          created_at: new Date().toISOString(),
        })),
      );

      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(wordSetSettingsQueryKey(userId!), context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: wordSetSettingsQueryKey(userId!) });
      // testDates 캐시에 settings가 묶여 있어 출제 범위 변경 시 목록 카테고리가 즉시 반영되도록 함
      queryClient.invalidateQueries({ queryKey: ["testDates", userId] });
    },
  });
}
